import React, { useEffect, useState } from 'react'
import { Badge, Button, Card, Col, Container, Form, Row, Spinner } from 'react-bootstrap'
import { Flag, People } from 'react-bootstrap-icons'
import { LuGraduationCap } from 'react-icons/lu'
import axios from 'axios'
import Cookies from 'js-cookie'
import toast from 'react-hot-toast'
import { useNavigate, useParams } from 'react-router-dom'
import 'react-bootstrap-typeahead/css/Typeahead.css'
import { Typeahead } from 'react-bootstrap-typeahead'

import { useAuth } from './AuthContext'
import { useSidebar } from './SidebarContext'
import ProfileDropdown from './commonComponents/ProfileDropdown'
import { useInstitute } from './commonComponents/InstituteContext'
import { BASE_URL } from './commonComponents/ApiEndpoints'
import {
  createAnnouncementRequest,
  datetimeLocalToIsoOrNull,
  fetchAnnouncementById,
  getAnnouncementErrorMessage,
  normalizeClassIdsFromApi,
  parseAnnouncementOne,
  toDatetimeLocalInput,
  uploadAnnouncementMedia,
  updateAnnouncementRequest,
} from './commonComponents/announcementApi'

export default function AnnouncementForm() {
  const { id: editId } = useParams()
  const isEditMode = Boolean(editId)

  const { logout, user } = useAuth()
  const { isCollapsed } = useSidebar()
  const navigate = useNavigate()
  const [marginLeft, setMarginLeft] = useState('0px')
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth <= 768 : false,
  )
  const [logoError, setLogoError] = useState(false)
  const { logo, id: instituteIdFromContext } = useInstitute()

  const [classesList, setClassesList] = useState([])
  const [coursesList, setCoursesList] = useState([])
  const [loadingClasses, setLoadingClasses] = useState(true)
  const [loadingEdit, setLoadingEdit] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [uploadLoading, setUploadLoading] = useState(false)
  const [selectedFile, setSelectedFile] = useState(null)
  const [mediaUrl, setMediaUrl] = useState('')
  const [mediaFileLabel, setMediaFileLabel] = useState('')

  const [form, setForm] = useState({
    scope: 'school',
    title: '',
    message: '',
    status: 'draft',
    publish_at: '',
    expire_at: '',
    class_id: '',
    course_id: '',
    media_link: '',
    priority: 'mid',
  })

  const instituteId = Cookies.get('institute_id') || instituteIdFromContext

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 992) {
        const expandedWidth = 245
        const collapsedWidth = 85
        const gap = 1
        setMarginLeft(
          isCollapsed ? `${collapsedWidth + gap}px` : `${expandedWidth + gap}px`,
        )
      } else {
        setMarginLeft('0px')
      }
      setIsMobile(window.innerWidth <= 768)
    }

    handleResize()
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [isCollapsed])

  useEffect(() => {
    const loadClassesAndCourses = async () => {
      setLoadingClasses(true)
      try {
        const institueId = Cookies.get('institute_id')
        const headers = {
          'institute-Id': Number(institueId),
          Authorization: `Bearer ${user?.token}`,
        }
        const [resClasses, resCourses] = await Promise.all([
          axios.get(`${BASE_URL}/class/all`, { headers }),
          axios.get(`${BASE_URL}/course/all`, { headers }),
        ])
        setClassesList(resClasses?.data?.data || [])
        setCoursesList(resCourses?.data?.data || [])
      } catch (e) {
        console.error(e)
        toast.error('Could not load classes or courses.')
        setClassesList([])
        setCoursesList([])
      } finally {
        setLoadingClasses(false)
      }
    }
    loadClassesAndCourses()
  }, [user?.token])

  useEffect(() => {
    if (!isEditMode || !editId) return

    const load = async () => {
      setLoadingEdit(true)
      try {
        const res = await fetchAnnouncementById(editId)
        const row = parseAnnouncementOne(res)
        if (!row) {
          toast.error('Announcement not found.')
          navigate('/announcements', { replace: true })
          return
        }
        const normalizedClassIds = normalizeClassIdsFromApi(row.class_ids)
        const resolvedClassId =
          row.class_id != null && row.class_id !== ''
            ? String(row.class_id)
            : normalizedClassIds.length
              ? String(normalizedClassIds[0])
              : ''
        let resolvedCourseId = ''
        if (row.course_id != null && row.course_id !== '') {
          resolvedCourseId = String(row.course_id)
        } else if (Array.isArray(row.course_ids) && row.course_ids.length) {
          const first = row.course_ids[0]
          resolvedCourseId =
            first != null && typeof first === 'object'
              ? String(first.id ?? first.course_id ?? '')
              : String(first)
        }
        setForm({
          scope:
            row.scope === 'class'
              ? 'class'
              : row.scope === 'course'
                ? 'course'
                : 'school',
          title: row.title || '',
          message: row.message ?? row.description ?? '',
          status: row.status || 'draft',
          publish_at: toDatetimeLocalInput(row.publish_at),
          expire_at: toDatetimeLocalInput(row.expire_at),
          class_id: resolvedClassId,
          course_id: resolvedCourseId,
          media_link: row.media_link || '',
          priority: row.priority || 'mid',
        })
        setMediaFileLabel(
          row.media_link && String(row.media_link).toLowerCase().includes('.pdf')
            ? 'Attached PDF'
            : row.media_link
              ? 'Attached image'
              : '',
        )
      } catch (err) {
        const msg = getAnnouncementErrorMessage(err, navigate)
        toast.error(msg)
        navigate('/announcements', { replace: true })
      } finally {
        setLoadingEdit(false)
      }
    }
    load()
  }, [editId, isEditMode, navigate])

  const handleChange = (e) => {
    const { name, value } = e.target
    if (name === 'scope') {
      setForm((prev) => ({
        ...prev,
        scope: value,
        class_id: '',
        course_id: '',
      }))
      return
    }
    setForm((prev) => ({ ...prev, [name]: value }))
  }

  const handleMediaFileChange = (e) => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file) return

    const isImage = ['image/jpeg', 'image/jpg', 'image/png'].includes(
      String(file.type).toLowerCase(),
    )
    const isPdf =
      file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf')
    if (!isImage && !isPdf) {
      toast.error('Please select JPEG, JPG, PNG, or PDF file.')
      return
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size should not exceed 5MB.')
      return
    }

    setSelectedFile(file)
    setMediaFileLabel(file.name)
    setForm((prev) => ({ ...prev, media_link: '' }))
    if (isImage) {
      setMediaUrl(URL.createObjectURL(file))
    } else {
      setMediaUrl('')
    }
  }

  const clearMedia = () => {
    setForm((prev) => ({ ...prev, media_link: '' }))
    setSelectedFile(null)
    setMediaUrl('')
    setMediaFileLabel('')
  }

  const validate = () => {
    if (!form.title.trim()) {
      toast.error('Title is required.')
      return false
    }
    if (!form.message.trim()) {
      toast.error('Message is required.')
      return false
    }
    if (form.scope === 'class' && !String(form.class_id || '').trim()) {
      toast.error('Please select a class when scope is Class.')
      return false
    }
    if (form.scope === 'course' && !String(form.course_id || '').trim()) {
      toast.error('Please select a course when scope is Course.')
      return false
    }
    if (!instituteId) {
      toast.error('Institute is not selected.')
      return false
    }
    return true
  }

  const buildBody = () => {
    const body = {
      institute_id: Number(instituteId),
      scope: form.scope,
      title: form.title.trim(),
      message: form.message.trim(),
      status: form.status,
      publish_at: datetimeLocalToIsoOrNull(form.publish_at),
      expire_at: datetimeLocalToIsoOrNull(form.expire_at),
      priority: form.priority,
    }
    if (form.media_link && String(form.media_link).trim()) {
      body.media_link = String(form.media_link).trim()
    }
    if (form.scope === 'class') {
      const cid = Number(form.class_id)
      body.class_id = cid
      body.class_ids = form.class_id ? [cid] : []
    } else if (form.scope === 'course') {
      const courseIdNum = Number(form.course_id)
      body.course_ids = form.course_id && !Number.isNaN(courseIdNum) ? [courseIdNum] : []
      body.class_ids = []
    } else {
      body.class_ids = []
    }
    return body
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!validate()) return

    setSubmitting(true)
    try {
      const body = buildBody()
      let targetAnnouncementId = editId
      if (isEditMode) {
        await updateAnnouncementRequest(editId, body)
        toast.success('Announcement updated.')
      } else {
        const createRes = await createAnnouncementRequest(body)
        const createdId =
          createRes?.data?.data?.id ??
          createRes?.data?.id ??
          createRes?.data?.announcement_id
        targetAnnouncementId = createdId
        toast.success('Announcement created.')
      }

      if (selectedFile && targetAnnouncementId) {
        setUploadLoading(true)
        const uploadRes = await uploadAnnouncementMedia(targetAnnouncementId, selectedFile)
        const uploadedUrl =
          uploadRes?.data?.url ??
          uploadRes?.data?.data?.url ??
          uploadRes?.data?.media_url ??
          ''
        if (!uploadedUrl) {
          toast.error('Media upload succeeded but URL was not returned.')
        } else {
          // Persist public URL as media_link on announcement
          await updateAnnouncementRequest(targetAnnouncementId, {
            ...body,
            media_link: uploadedUrl,
          })
        }
        setForm((prev) => ({
          ...prev,
          media_link: uploadedUrl || prev.media_link,
        }))
        setMediaUrl(uploadedUrl || mediaUrl)
        toast.success('Media uploaded successfully.')
      } else if (selectedFile && !targetAnnouncementId) {
        toast.error('Announcement created but media upload could not start.')
      }

      navigate('/announcements')
    } catch (err) {
      const msg = getAnnouncementErrorMessage(err, navigate)
      toast.error(msg)
    } finally {
      setUploadLoading(false)
      setSubmitting(false)
    }
  }

  const pageBusy = loadingEdit || (loadingClasses && !isEditMode)

  return (
    <>
      <style>
        {`
          .page-title {
            font-family: 'Inter', system-ui, sans-serif;
            letter-spacing: -0.02em;
            color: #223843;
          }
          .form-control, .form-select {
            border-radius: 10px !important;
            border: 1px solid #D1D3D4 !important;
            padding: 10px 12px !important;
            font-family: 'Inter', system-ui, sans-serif;
          }
          .form-label {
            font-weight: 600;
            color: #111827;
            font-family: 'Inter', system-ui, sans-serif;
          }
          @media (max-width: 768px) {
            .announcement-form-shell {
              width: 100% !important;
              max-width: 100% !important;
              padding-left: 0 !important;
              padding-right: 0 !important;
              box-sizing: border-box !important;
            }
            .announcement-form-container {
              width: 100% !important;
              max-width: 100% !important;
              margin-left: 0 !important;
              margin-right: 0 !important;
              padding-left: 12px !important;
              padding-right: 12px !important;
              box-sizing: border-box !important;
            }
          }
        `}
      </style>

      <div
        className="announcement-form-shell"
        style={{
          marginLeft,
          transition: 'margin-left 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
          willChange: 'margin-left',
          position: 'relative',
          zIndex: 1,
          marginTop: '0px',
          paddingLeft: isMobile ? '0px' : '12px',
          paddingRight: isMobile ? '0px' : '12px',
          width: isMobile ? '100%' : undefined,
          maxWidth: isMobile ? '100%' : undefined,
          boxSizing: 'border-box',
        }}
      >
        <div
          className="container-fluid p-0"
          style={{
            marginTop: window.innerWidth < 992 ? '55px' : '-12px',
            paddingLeft: '0px',
            paddingRight: '0px',
          }}
        >
          <div style={{ marginLeft: isMobile ? '-13px' : '-40px' }}>
            <div
              className="d-flex justify-content-between align-items-center"
              style={{
                background: '#F3F4F6',
                paddingLeft: '28px',
                paddingRight: '28px',
                paddingTop: '12px',
                paddingBottom: '12px',
                borderBottom: '1px solid #D1D3D4',
                width: '100%',
                gap: '12px',
              }}
            >
              <div className="d-flex align-items-center gap-3" style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    overflow: 'hidden',
                    background: '#223843',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  {!logoError ? (
                    <img
                      src={logo}
                      alt="School Logo"
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      onError={() => setLogoError(true)}
                    />
                  ) : (
                    <LuGraduationCap size={20} style={{ color: '#ffffff' }} />
                  )}
                </div>
                <div style={{ minWidth: 0 }}>
                  <h3
                    className="m-0 fw-bold page-title"
                    style={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      fontSize: isMobile ? '14px' : 'clamp(18px, 2.5vw, 22px)',
                      lineHeight: '1.2',
                    }}
                  >
                    {isEditMode ? 'Edit Announcement' : 'Create Announcement'}
                  </h3>
                  <p
                    className="m-0 mt-0 text-start"
                    style={{ fontSize: isMobile ? '10px' : '13px', color: '#6b7280' }}
                  >
                    {isEditMode ? 'Update details and save.' : 'Fill details and publish.'}
                  </p>
                </div>
              </div>

              {!isMobile && <ProfileDropdown logout={logout} notificationCount={3} showNotifications={true} />}
            </div>
          </div>
        </div>

        <Container
           fluid={isMobile}
          className="mt-4 announcement-form-container"
          style={{
            paddingLeft: isMobile ? '12px' : isCollapsed ? '32px' : '40px',
            paddingRight: isMobile ? '12px' : '32px',
            paddingTop: isMobile ? '16px' : '32px',
            paddingBottom: isMobile ? '16px' : '32px',
            maxWidth: isMobile ? '100%' : '1200px',
            width: '100%',
            marginLeft: isMobile ? 0 : undefined,
            marginRight: isMobile ? 0 : undefined,
            boxSizing: 'border-box',
          }}
        >
          {pageBusy ? (
            <div className="d-flex justify-content-center py-5">
              <Spinner animation="border" variant="primary" />
            </div>
          ) : (
            <Form onSubmit={handleSubmit}>
              <Row className="g-4">
                <Col xs={12} md={6}>
                  <Card
                    style={{
                      border: '1px solid #D1D3D4',
                      borderRadius: '12px',
                      boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
                      overflow: 'hidden',
                    }}
                  >
                    <div
                      style={{
                        background: 'linear-gradient(135deg, #223843 0%, #223843 100%)',
                        padding: isMobile ? '14px 16px' : '16px 24px',
                        borderBottom: '1px solid #D1D3D4',
                      }}
                    >
                      <div className="d-flex align-items-center justify-content-between">
                        <div className="d-flex align-items-center gap-2">
                          <div
                            style={{
                              width: '36px',
                              height: '36px',
                              borderRadius: '10px',
                              backgroundColor: 'rgba(255, 255, 255, 0.2)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <Flag size={18} style={{ color: '#ffffff' }} />
                          </div>
                          <h5
                            style={{
                              margin: 0,
                              fontWeight: '700',
                              fontSize: '16px',
                              color: '#ffffff',
                              fontFamily: 'Inter, system-ui, sans-serif',
                            }}
                          >
                            Content
                          </h5>
                        </div>
                        <Badge bg="light" text="primary" style={{ border: '1px solid rgba(255,255,255,0.4)' }}>
                          {isEditMode ? 'Edit' : 'New'}
                        </Badge>
                      </div>
                    </div>

                    <Card.Body style={{ padding: isMobile ? '16px' : '24px' }} className="text-start">
                      <Form.Group className="mb-3">
                        <Form.Label>Title *</Form.Label>
                        <Form.Control
                          name="title"
                          value={form.title}
                          onChange={handleChange}
                          placeholder="Announcement title"
                          required
                        />
                      </Form.Group>

                      <Form.Group className="mb-3">
                        <Form.Label>Message *</Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={6}
                          name="message"
                          value={form.message}
                          onChange={handleChange}
                          placeholder="Write the announcement message..."
                          required
                        />
                      </Form.Group>

                      <Form.Group className="mb-0">
                        <Form.Label>Media upload (optional)</Form.Label>
                        <Form.Control
                          type="file"
                          accept=".jpeg,.jpg,.png,.pdf"
                          onChange={handleMediaFileChange}
                          disabled={uploadLoading || submitting}
                        />
                        <Form.Text className="text-muted">
                          Allowed: JPEG/JPG/PNG/PDF, max 5MB.
                        </Form.Text>
                        {selectedFile ? (
                          <div className="mt-2 small text-muted text-break">
                            Selected file: {mediaFileLabel}
                          </div>
                        ) : null}
                        {uploadLoading ? (
                          <div className="d-flex align-items-center gap-2 mt-2">
                            <Spinner animation="border" size="sm" />
                            <span className="text-muted small">Uploading media…</span>
                          </div>
                        ) : null}
                        {((mediaUrl && selectedFile) || form.media_link) && !uploadLoading ? (
                          <div className="mt-2">
                            {/\.pdf(\?|$)/i.test(mediaUrl || form.media_link) ||
                            /\.pdf$/i.test(mediaFileLabel || '') ? (
                              <div
                                className="small text-break"
                                style={{ color: '#374151' }}
                              >
                                {mediaFileLabel || 'PDF attached'}
                              </div>
                            ) : (
                              <img
                                src={mediaUrl || form.media_link}
                                alt="Attachment preview"
                                style={{
                                  maxHeight: '120px',
                                  borderRadius: '8px',
                                  border: '1px solid #D1D3D4',
                                  objectFit: 'contain',
                                }}
                              />
                            )}
                            <div>
                              <Button
                                type="button"
                                variant="outline-secondary"
                                size="sm"
                                className="mt-2"
                                onClick={clearMedia}
                              >
                                Remove file
                              </Button>
                            </div>
                          </div>
                        ) : null}
                      </Form.Group>
                    </Card.Body>
                  </Card>
                </Col>

                <Col xs={12} md={6}>
                  <Card
                    style={{
                      border: '1px solid #D1D3D4',
                      borderRadius: '12px',
                      boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
                      overflow: 'hidden',
                    }}
                  >
                    <div
                      style={{
                        background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
                        padding: isMobile ? '14px 16px' : '16px 24px',
                        borderBottom: '1px solid #D1D3D4',
                      }}
                    >
                      <div className="d-flex align-items-center gap-2">
                        <div
                          style={{
                            width: '36px',
                            height: '36px',
                            borderRadius: '10px',
                            backgroundColor: 'rgba(255, 255, 255, 0.2)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <People size={18} style={{ color: '#ffffff' }} />
                        </div>
                        <h5
                          style={{
                            margin: 0,
                            fontWeight: '700',
                            fontSize: '16px',
                            color: '#ffffff',
                            fontFamily: 'Inter, system-ui, sans-serif',
                          }}
                        >
                          Scope & status
                        </h5>
                      </div>
                    </div>

                    <Card.Body style={{ padding: isMobile ? '16px' : '24px' }} className="text-start">
                      <Form.Group className="mb-3">
                        <Form.Label>Scope *</Form.Label>
                        <Form.Select name="scope" value={form.scope} onChange={handleChange} required>
                          <option value="school">School (all)</option>
                          <option value="class">Class (specific)</option>
                          <option value="course">Course (specific)</option>
                        </Form.Select>
                      </Form.Group>

                      <div
                        className="mb-3"
                        style={{
                          minHeight: form.scope === 'school' ? 0 : 88,
                          transition: 'min-height 0.15s ease',
                        }}
                      >
                        {form.scope === 'course' ? (
                          <Form.Group className="mb-0">
                            <Form.Label>Course *</Form.Label>
                            <div style={{ position: 'relative' }}>
                              <Typeahead
                                id="announcement-form-course"
                                labelKey="name"
                                onChange={(selected) => {
                                  const course = selected[0]
                                  setForm((prev) => ({
                                    ...prev,
                                    course_id: course ? String(course.id) : '',
                                  }))
                                }}
                                options={coursesList}
                                placeholder="Select course"
                                positionFixed
                                selected={
                                  form.course_id
                                    ? coursesList.filter(
                                        (c) => String(c.id) === String(form.course_id),
                                      )
                                    : []
                                }
                                inputProps={{
                                  style: {
                                    borderRadius: '8px',
                                    border: '1px solid #D1D3D4',
                                    fontSize: '14px',
                                    minHeight: '38px',
                                  },
                                }}
                              />
                            </div>
                          </Form.Group>
                        ) : null}
                        {form.scope === 'class' ? (
                          <Form.Group className="mb-0">
                            <Form.Label>Class *</Form.Label>
                            <div style={{ position: 'relative' }}>
                              <Typeahead
                                id="announcement-form-class"
                                labelKey={(c) =>
                                  c.class_name || c.name || `Class ${c.id ?? c.class_id}`
                                }
                                onChange={(selected) => {
                                  const c = selected[0]
                                  const rawId = c ? c.id ?? c.class_id : ''
                                  setForm((prev) => ({
                                    ...prev,
                                    class_id:
                                      rawId !== '' && rawId != null ? String(rawId) : '',
                                  }))
                                }}
                                options={classesList}
                                placeholder="Select class"
                                positionFixed
                                selected={
                                  form.class_id
                                    ? classesList.filter(
                                        (c) =>
                                          String(c.id ?? c.class_id) ===
                                          String(form.class_id),
                                      )
                                    : []
                                }
                                inputProps={{
                                  style: {
                                    borderRadius: '8px',
                                    border: '1px solid #D1D3D4',
                                    fontSize: '14px',
                                    minHeight: '38px',
                                  },
                                }}
                              />
                            </div>
                          </Form.Group>
                        ) : null}
                      </div>

                      <Form.Group className="mb-3">
                        <Form.Label>Priority</Form.Label>
                        <Form.Select name="priority" value={form.priority} onChange={handleChange}>
                          <option value="low">Low</option>
                          <option value="mid">Mid</option>
                          <option value="high">High</option>
                        </Form.Select>
                      </Form.Group>

                      <Form.Group className="mb-3">
                        <Form.Label>Status *</Form.Label>
                        <Form.Select name="status" value={form.status} onChange={handleChange} required>
                          <option value="draft">Draft</option>
                          <option value="published">Published</option>
                          <option value="archived">Archived</option>
                        </Form.Select>
                      </Form.Group>

                      <Form.Group className="mb-3">
                        <Form.Label>Publish at</Form.Label>
                        <Form.Control
                          type="datetime-local"
                          name="publish_at"
                          value={form.publish_at}
                          onChange={handleChange}
                        />
                      </Form.Group>

                      <Form.Group className="mb-0">
                        <Form.Label>Expire at</Form.Label>
                        <Form.Control
                          type="datetime-local"
                          name="expire_at"
                          value={form.expire_at}
                          onChange={handleChange}
                        />
                      </Form.Group>
                    </Card.Body>
                  </Card>
                </Col>

              </Row>

              <div
                className={`d-flex gap-3 mt-4 pt-3 ${isMobile ? 'flex-column' : 'justify-content-end'}`}
                style={{ borderTop: '1px solid #E5E7EB' }}
              >
                <Button
                  type="button"
                  variant="light"
                  className="border"
                  disabled={submitting}
                  onClick={() => navigate('/announcements')}
                  style={{
                    borderRadius: '8px',
                    padding: '12px 24px',
                    fontWeight: '600',
                    minWidth: isMobile ? undefined : '140px',
                    width: isMobile ? '100%' : undefined,
                  }}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={submitting || uploadLoading}
                  style={{
                    backgroundColor: '#10B981',
                    border: 'none',
                    color: '#ffffff',
                    borderRadius: '8px',
                    padding: '12px 24px',
                    fontWeight: '600',
                    minWidth: isMobile ? undefined : '160px',
                    width: isMobile ? '100%' : undefined,
                  }}
                >
                  {submitting || uploadLoading ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      {uploadLoading ? 'Uploading media…' : 'Saving…'}
                    </>
                  ) : isEditMode ? (
                    'Save changes'
                  ) : (
                    'Create announcement'
                  )}
                </Button>
              </div>
            </Form>
          )}
        </Container>
      </div>
    </>
  )
}
